import { z } from "zod";
import {
  bmiQuizV11,
  bmiQuizV12,
  bmiQuizV2,
  bmiQuizWithoutDesiredSupport,
  bmiQuizWithoutSafetyQuestions,
  bmiQuizWithoutTransferQuestion,
  readDesiredOutcome,
  visibleBmiQuizFields,
  type BmiQuizAnswerValue,
  type BmiQuizField,
  type BmiQuizTemplate,
} from "./bmi-quiz";

// TES-1326 — the F-01 quality and product insight view in admin.
//
// Emelie (PUA) 2026-10-13, logged in TES-475: the purpose is follow-up,
// evaluation and product development of the care service, plus business
// statistics. There is no separate patient-data extract. The view shows
// counts, shares, non-response and approved group comparisons, with these
// rules from the decision:
//   "Grupper med färre än 10 personer döljs."
//   "Kombinationer med källa/UTM, livsfas eller annan känslig variabel kräver
//    minst 20 personer."
//   "Det ska inte gå att klicka vidare från en siffra till en patientlista."
//   "Rå fritext visas inte i insiktsvyn."
//
// Every rule lives in this file and runs on the server before the response
// leaves. The report type holds numbers, codes and labels only. There is no
// id, no free text and no per-person row, so nothing in it can be followed
// back to a patient.

/** "Grupper med färre än 10 personer döljs." */
export const F01_INSIGHTS_MIN_GROUP = 10;
/** Source, life stage and other sensitive variables: at least 20. */
export const F01_INSIGHTS_MIN_SENSITIVE_GROUP = 20;
/**
 * Shown counts are rounded to the nearest 5. A hidden cell can otherwise be
 * recovered by subtraction between overlapping views: a quarter minus two of
 * its months, or "booked a meeting" minus "completed a meeting". Rounding
 * leaves an uncertainty of a few people in every such difference, so the
 * result never pins down a small group. Shares are computed from the exact
 * counts and shown as whole percentages.
 */
export const F01_INSIGHTS_ROUND_TO = 5;

/**
 * The questions the view may show. This is an allowlist: a key that is not
 * listed here is never read. `sensitive` raises a question's threshold to
 * {@link F01_INSIGHTS_MIN_SENSITIVE_GROUP}, and every answer pair that
 * includes it inherits that threshold.
 */
export const F01_INSIGHT_QUESTIONS = [
  { key: "impactDegree", sensitive: false },
  { key: "desiredSupport", sensitive: false },
  { key: "desiredOutcome", sensitive: false },
  { key: "howLongTried", sensitive: false },
  // TES-1658 (v13) — women-specific life phases, so "livsfas": sensitive.
  { key: "weightChangeLifePhase", sensitive: true },
  { key: "weightDifficulties", sensitive: false },
  { key: "changeContext", sensitive: false },
  { key: "methodsTried", sensitive: false },
  { key: "biggestObstacles", sensitive: false },
  { key: "bodySignals", sensitive: false },
  { key: "emotionalEating", sensitive: false },
  // "livsfas"
  { key: "hormonalLifePhase", sensitive: true },
  { key: "lifeStage", sensitive: true },
  // "källa"
  { key: "howFoundUs", sensitive: true },
] as const satisfies ReadonlyArray<{ key: string; sensitive: boolean }>;

export type F01InsightQuestionKey =
  (typeof F01_INSIGHT_QUESTIONS)[number]["key"];

/**
 * TES-1371 (Emelie, Ö-543, 2026-10-15): prior prescription weight treatment
 * "får därför förberedas som en känsligare aggregerad variabel, med: minst
 * 20 personer, kompletterande undertryckning och avrundning, ingen
 * individkoppling, ingen radvis export". "Aktivera den inte i produktion
 * förrän Zeinab har bekräftat nödvändighet, rättslig grund och skyddsregel."
 *
 * So it is read only when the caller passes `weightMedHistory: true` (the
 * API's own switch, off by default), and then with stricter rules than the
 * other questions:
 * - every count needs {@link F01_INSIGHTS_MIN_SENSITIVE_GROUP}, in every
 *   group of every breakdown;
 * - two-way complementary suppression ({@link suppressTwoWay}): a hidden
 *   cell can be derived neither from its group's total, nor from the other
 *   groups' counts for the same answer, nor as a group minus a group nested
 *   in it;
 * - shares are computed from the rounded counts, so an exact percentage
 *   cannot undo the rounding;
 * - it never enters the answer-pair list, so no combination with another
 *   question is shown at all.
 */
export const F01_INSIGHT_GATED_QUESTIONS = [
  { key: "weightMedHistory", sensitive: true },
] as const satisfies ReadonlyArray<{ key: string; sensitive: boolean }>;

/**
 * Keys the view never shows, with the reason. The allowlist above already
 * keeps them out. This list exists so a test can prove it, and so the next
 * person to add a question sees the boundary.
 *
 * - Weight, height and BMI, reproductive status, type 1 diabetes, MTC/MEN2
 *   and prescription transfer are named in the decision.
 * - Free text: "Rå fritext visas inte i insiktsvyn."
 *
 * weightMedHistory left this list with TES-1371. It is a gated question
 * ({@link F01_INSIGHT_GATED_QUESTIONS}), read only behind its own switch.
 */
export const F01_INSIGHTS_EXCLUDED_KEYS = [
  "weightKg",
  "heightCm",
  "reproductiveStatus",
  "type1Diabetes",
  "mtcMen2",
  "prescriptionTransfer",
  "desiredOutcomeOther",
  "desiredSupportOther",
  "howFoundUsOther",
] as const;

// Funnel steps, counted "reached by now". The groups overlap. A prescription
// transfer can start a subscription without ever booking a meeting.
export const F01_INSIGHT_COHORTS = [
  "booked_meeting",
  "completed_meeting",
  "started_subscription",
] as const;
export type F01InsightCohort = (typeof F01_INSIGHT_COHORTS)[number];

export const F01_INSIGHT_CHANNELS = [
  "web_mobile",
  "web_desktop",
  "web_unknown",
  "app",
] as const;
export type F01InsightChannel = (typeof F01_INSIGHT_CHANNELS)[number];

/**
 * The approved comparisons (Ö-545 assumption). Every question can be split
 * by funnel step, by channel or by form version, and nothing else. There is
 * no free filter and no cross-tabulation of two questions except the
 * answer-pair list.
 */
export const F01_INSIGHT_BREAKDOWNS = ["cohort", "channel", "version"] as const;
export type F01InsightBreakdown = (typeof F01_INSIGHT_BREAKDOWNS)[number];

export const F01_INSIGHT_MODES = ["synthetic", "live"] as const;
export type F01InsightMode = (typeof F01_INSIGHT_MODES)[number];

/**
 * TES-1403 (Emelie, Ö-542, 2026-10-15): "Även webbsvar från personer som
 * ännu inte blivit patienter ska kunna användas aggregerat för
 * produktutveckling, kvalitet och patientsäkerhet. Dom får aldrig användas
 * för individuell marknadsföring, individuell kommersiell profilering eller
 * marknadsurval." And: "Patientdata och icke-patientdata ska förbli
 * särskiljbara".
 *
 * So the view has two populations, and one report only ever holds one of
 * them:
 * - `patients`: every real patient with an F-01, once each (TES-1326);
 * - `web_unidentified`: web quiz answers ("Webb, ej identifierad") that were
 *   never linked to a patient. They carry no identity at all, so they cannot
 *   be deduplicated against each other or against the patients. A sum of the
 *   two would count some people twice, which is one more reason never to add
 *   them up.
 *
 * {@link buildF01InsightReport} refuses respondents from another population
 * than the one it was asked for.
 */
export const F01_INSIGHT_POPULATIONS = [
  "patients",
  "web_unidentified",
] as const;
export type F01InsightPopulation = (typeof F01_INSIGHT_POPULATIONS)[number];

export const F01_INSIGHT_POPULATION_LABELS: Record<
  F01InsightPopulation,
  string
> = {
  patients: "Patienter",
  web_unidentified: "Webb, ej identifierad",
};

/**
 * The breakdowns each population allows. The funnel steps are a patient's
 * (booked, held, subscribed), so a web answer that never became a patient
 * has none of them and can only be split by channel or form version.
 */
export const F01_INSIGHT_POPULATION_BREAKDOWNS: Record<
  F01InsightPopulation,
  readonly F01InsightBreakdown[]
> = {
  patients: F01_INSIGHT_BREAKDOWNS,
  web_unidentified: ["channel", "version"],
};

/** The web channels: the only channels a web answer can have. */
export const F01_INSIGHT_WEB_CHANNELS = [
  "web_mobile",
  "web_desktop",
  "web_unknown",
] as const satisfies readonly F01InsightChannel[];

// Fixed periods only (Ö-544 assumption): a calendar month, a quarter, or
// everything. A free date range would let two almost identical ranges be
// subtracted down to a single day.
export const F01_INSIGHT_PERIOD_RE =
  /^(all|\d{4}-(0[1-9]|1[0-2])|\d{4}-Q[1-4])$/;

/**
 * The instruments this view can read, by stamped version. Only these field
 * sets are reproducible from code (bmi-quiz.ts). An answer set stamped
 * anything else was given against an instrument whose option lists differ.
 * It is counted as "older version" and never read.
 */
const INSTRUMENTS: ReadonlyMap<number, BmiQuizTemplate> = new Map([
  [bmiQuizWithoutTransferQuestion.version, bmiQuizWithoutTransferQuestion],
  [bmiQuizWithoutSafetyQuestions.version, bmiQuizWithoutSafetyQuestions],
  [bmiQuizWithoutDesiredSupport.version, bmiQuizWithoutDesiredSupport],
  // TES-1533 — v12 only rewords the weight question; v11 keeps its own.
  [bmiQuizV11.version, bmiQuizV11],
  // TES-1658 — v13 replaced six questions; v12 keeps its own.
  [bmiQuizV12.version, bmiQuizV12],
  [bmiQuizV2.version, bmiQuizV2],
]);

export function f01InsightInstrument(
  version: number | null,
): BmiQuizTemplate | null {
  return version === null ? null : (INSTRUMENTS.get(version) ?? null);
}

export const F01_INSIGHT_VERSIONS: readonly number[] = [
  ...INSTRUMENTS.keys(),
].sort((a, b) => a - b);

/**
 * One person's newest F-01, as the server hands it to {@link
 * buildF01InsightReport}. This value never leaves the server. The report
 * built from it holds no field of it except counts.
 */
export interface F01InsightRespondent {
  /**
   * TES-1403 — which population this answer set belongs to. Absent means
   * `patients` (every respondent before TES-1403 was one).
   */
  population?: F01InsightPopulation;
  answers: Record<string, unknown>;
  templateVersion: number | null;
  /** Stockholm calendar day she answered, YYYY-MM-DD. */
  answeredOn: string;
  channel: F01InsightChannel;
  cohorts: Readonly<Record<F01InsightCohort, boolean>>;
}

// --- report --------------------------------------------------------------

/**
 * A count as shown. `n` is rounded to {@link F01_INSIGHTS_ROUND_TO} and is
 * null when the exact count is under `min`. A zero is shown as 0: it is not a
 * group of anyone.
 */
export const f01InsightCellSchema = z
  .object({
    n: z.number().int().nonnegative().nullable(),
    min: z.number().int(),
  })
  .strict();
export type F01InsightCell = z.infer<typeof f01InsightCellSchema>;

const optionRowSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    count: f01InsightCellSchema,
    /** Whole percent of those who answered the question in this group. */
    share: z.number().int().min(0).max(100).nullable(),
  })
  .strict();

const questionGroupSchema = z
  .object({
    group: z.string(),
    /** Their version asked the question. */
    asked: f01InsightCellSchema,
    answered: f01InsightCellSchema,
    /** Asked but left unanswered (optional questions only). */
    nonResponse: f01InsightCellSchema,
    nonResponseShare: z.number().int().min(0).max(100).nullable(),
    /** True when too few answered to show anything for this group. */
    hidden: z.boolean(),
    /** No one in this group was given a version that asked it. */
    notAsked: z.boolean(),
    options: z.array(optionRowSchema),
  })
  .strict();

const questionSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    kind: z.enum(["single", "multi"]),
    sensitive: z.boolean(),
    groups: z.array(questionGroupSchema),
  })
  .strict();

const pairSideSchema = z
  .object({
    question: z.string(),
    questionLabel: z.string(),
    value: z.string(),
    label: z.string(),
  })
  .strict();

export const f01InsightReportSchema = z
  .object({
    mode: z.enum(F01_INSIGHT_MODES),
    /** TES-1403 — the one population every number in this report counts. */
    population: z.enum(F01_INSIGHT_POPULATIONS),
    /** The populations this box lets the view switch between. */
    populations: z.array(z.enum(F01_INSIGHT_POPULATIONS)),
    period: z.string().regex(F01_INSIGHT_PERIOD_RE),
    breakdown: z.enum(F01_INSIGHT_BREAKDOWNS),
    /** Selectable periods, newest first, "all" last. */
    periods: z.array(z.string().regex(F01_INSIGHT_PERIOD_RE)),
    rules: z
      .object({
        minGroup: z.number().int(),
        minSensitiveGroup: z.number().int(),
        roundTo: z.number().int(),
      })
      .strict(),
    respondents: f01InsightCellSchema,
    /** Answer sets from an instrument this view cannot read (before v8). */
    olderVersions: f01InsightCellSchema,
    groups: z.array(
      z
        .object({
          key: z.string(),
          label: z.string(),
          respondents: f01InsightCellSchema,
        })
        .strict(),
    ),
    questions: z.array(questionSchema),
    /** The most common answer pairs among all respondents (two answers, never three). */
    pairs: z.array(
      z
        .object({
          a: pairSideSchema,
          b: pairSideSchema,
          count: f01InsightCellSchema,
          share: z.number().int().min(0).max(100).nullable(),
        })
        .strict(),
    ),
  })
  .strict();
export type F01InsightReport = z.infer<typeof f01InsightReportSchema>;

// --- suppression ---------------------------------------------------------

export function roundCount(n: number): number {
  return Math.round(n / F01_INSIGHTS_ROUND_TO) * F01_INSIGHTS_ROUND_TO;
}

/** One count, shown or hidden. Zero is shown. */
export function cell(n: number, min: number): F01InsightCell {
  return { n: n === 0 ? 0 : n < min ? null : roundCount(n), min };
}

function share(n: number, of: number, shown: boolean): number | null {
  return shown && of > 0 ? Math.round((n / of) * 100) : null;
}

/**
 * The cells of a partition whose total is shown: the options of a
 * single-select question, which add up to "answered". Every count under
 * `min` is hidden. Then, while the hidden cells together hold fewer than
 * `min` people, the smallest shown cell is hidden too. Otherwise the total
 * minus the shown cells would recover the hidden group.
 *
 * Returns which cells to show. Zeros stay shown, because they hold nobody.
 */
export function suppressPartition(
  counts: readonly number[],
  min: number,
): boolean[] {
  const show = counts.map((n) => n === 0 || n >= min);
  const hiddenMass = () =>
    counts.reduce((sum, n, i) => (show[i] ? sum : sum + n), 0);
  while (hiddenMass() > 0 && hiddenMass() < min) {
    let smallest = -1;
    counts.forEach((n, i) => {
      if (show[i] && n > 0 && (smallest < 0 || n < counts[smallest])) {
        smallest = i;
      }
    });
    if (smallest < 0) break;
    show[smallest] = false;
  }
  return show;
}

/**
 * TES-1371 — two-way complementary suppression for a gated question.
 *
 * `counts[g][o]` is answer o in group g and `totals[g]` the people in group
 * g who answered. The question is single-select, so a group's counts add up
 * to its total. Group 0 is everyone. `partitioned` means the other groups
 * split group 0 without overlap (channel, version), so for every answer
 * group 0's count is the sum of the other groups' counts. `nested` lists
 * [parent, child] pairs whose child group lies wholly inside the parent.
 *
 * Start: a group whose total is under `min` is hidden whole, and every
 * count under `min` is hidden. Then more is hidden, one cell or group at a
 * time, until none of these holds:
 * 1. Mass. In any line whose sum is known (a shown group's counts against
 *    its total; when partitioned, the groups against everyone, and each
 *    answer's counts against everyone's count for it), the hidden entries
 *    hold between 1 and `min - 1` people together. A hidden count of
 *    everyone that is just the sum of shown parts counts as such a line.
 * 2. Nesting. For a nested pair, parent minus child is between 1 and
 *    `min - 1` while both are shown, because that difference is a group.
 * 3. Exact derivation. A hidden count can be solved from the shown numbers.
 *    The hidden counts are the edges of a graph whose nodes are the lines
 *    with a known sum. Every line with an unknown sum is merged into one
 *    free node. A count can be solved exactly when its edge is a bridge,
 *    and cannot be when its edge lies on a cycle: adding and subtracting
 *    the same amount around the cycle keeps every known sum. So a bridge
 *    gets a partner hidden in its own group or answer line.
 * Cells are only ever hidden, never shown again, so the loop ends. If no
 * partner is left, everything is hidden.
 *
 * What remains is a range, not a number, plus the rounding of every shown
 * count to {@link F01_INSIGHTS_ROUND_TO}.
 *
 * Returns which group totals and which counts may be shown.
 */
export function suppressTwoWay(input: {
  counts: readonly (readonly number[])[];
  totals: readonly number[];
  min: number;
  partitioned: boolean;
  nested: ReadonlyArray<readonly [number, number]>;
}): { groups: boolean[]; cells: boolean[][] } {
  const { counts, totals, min, partitioned, nested } = input;
  const groups = totals.map((t) => t >= min);
  const cells = counts.map((row, g) =>
    row.map((n) => groups[g] && (n === 0 || n >= min)),
  );
  const small = (mass: number) => mass > 0 && mass < min;
  const parts = counts.map((_, g) => g).slice(1);
  const answers = (counts[0] ?? []).map((_, o) => o);

  type Step =
    | { kind: "cell"; g: number; o: number }
    | { kind: "group"; g: number }
    | { kind: "all" };

  // The shown, non-zero cell with the smallest count among `candidates`.
  const smallestCell = (candidates: Array<[number, number]>): Step | null => {
    let best: Step | null = null;
    let bestN = Infinity;
    for (const [g, o] of candidates) {
      const n = counts[g][o];
      if (cells[g][o] && n > 0 && n < bestN) {
        best = { kind: "cell", g, o };
        bestN = n;
      }
    }
    return best;
  };

  const nextStep = (): Step | null => {
    // 1. Mass, within each shown group.
    for (let g = 0; g < counts.length; g++) {
      if (!groups[g]) continue;
      const hidden = answers.reduce(
        (s, o) => (cells[g][o] ? s : s + counts[g][o]),
        0,
      );
      if (small(hidden)) {
        return smallestCell(answers.map((o) => [g, o])) ?? { kind: "group", g };
      }
    }
    // 1. Mass, across groups.
    if (partitioned && groups[0]) {
      const hidden = parts.reduce((s, g) => (groups[g] ? s : s + totals[g]), 0);
      if (small(hidden)) {
        let pick = -1;
        for (const g of parts) {
          if (
            groups[g] &&
            totals[g] > 0 &&
            (pick < 0 || totals[g] < totals[pick])
          ) {
            pick = g;
          }
        }
        return pick < 0 ? { kind: "all" } : { kind: "group", g: pick };
      }
      for (const o of answers) {
        const hiddenParts = parts.reduce(
          (s, g) => (cells[g][o] ? s : s + counts[g][o]),
          0,
        );
        const leaks = cells[0][o]
          ? small(hiddenParts)
          : counts[0][o] > 0 && hiddenParts === 0;
        if (leaks) {
          return smallestCell(parts.map((g) => [g, o])) ?? { kind: "all" };
        }
      }
    }
    // 2. Nesting.
    for (const [p, c] of nested) {
      if (groups[p] && groups[c] && small(totals[p] - totals[c])) {
        return { kind: "group", g: c };
      }
      for (const o of answers) {
        if (cells[p][o] && cells[c][o] && small(counts[p][o] - counts[c][o])) {
          return { kind: "cell", g: c, o };
        }
      }
    }
    // 3. Exact derivation.
    for (const [g, o] of solvableCells()) {
      const partners: Array<[number, number]> = answers.map((x) => [g, x]);
      if (partitioned) for (const x of parts) partners.push([x, o]);
      return smallestCell(partners) ?? { kind: "all" };
    }
    return null;
  };

  // Hidden non-zero counts whose edge is a bridge, see 3. above.
  const solvableCells = (): Array<[number, number]> => {
    const FREE = "free";
    // Lines with an unknown sum are free, except that when everyone's total
    // is shown, all answers (or groups) with a hidden sum still add up to a
    // known remainder, so they form one line together.
    const answerNode = (o: number) =>
      !partitioned ? FREE : cells[0][o] ? `a${o}` : groups[0] ? "a*" : FREE;
    const groupNode = (g: number) =>
      groups[g] ? `g${g}` : partitioned && groups[0] ? "g*" : FREE;
    const edges: Array<{ g: number; o: number; a: string; b: string }> = [];
    const inner = partitioned ? parts : counts.map((_, g) => g);
    for (const g of inner) {
      for (const o of answers) {
        if (!cells[g][o]) {
          edges.push({ g, o, a: groupNode(g), b: answerNode(o) });
        }
      }
    }
    const bridges = bridgeEdges(edges);
    return edges
      .filter((e, i) => bridges.has(i) && counts[e.g][e.o] > 0)
      .map((e) => [e.g, e.o]);
  };

  for (let step = nextStep(); step; step = nextStep()) {
    if (step.kind === "cell") {
      cells[step.g][step.o] = false;
    } else if (step.kind === "group") {
      groups[step.g] = false;
      cells[step.g] = cells[step.g].map(() => false);
    } else {
      return {
        groups: groups.map(() => false),
        cells: cells.map((row) => row.map(() => false)),
      };
    }
  }
  return { groups, cells };
}

/**
 * The indexes of the edges that lie on no cycle, in a multigraph given as
 * an edge list. Parallel edges form a cycle and a self-loop is one.
 */
function bridgeEdges(
  edges: ReadonlyArray<{ a: string; b: string }>,
): Set<number> {
  const adjacent = new Map<string, Array<{ to: string; edge: number }>>();
  edges.forEach(({ a, b }, edge) => {
    if (!adjacent.has(a)) adjacent.set(a, []);
    if (!adjacent.has(b)) adjacent.set(b, []);
    adjacent.get(a)!.push({ to: b, edge });
    if (a !== b) adjacent.get(b)!.push({ to: a, edge });
  });
  const order = new Map<string, number>();
  const low = new Map<string, number>();
  const bridges = new Set<number>();
  let clock = 0;
  const visit = (node: string, via: number) => {
    order.set(node, clock);
    low.set(node, clock);
    clock += 1;
    for (const { to, edge } of adjacent.get(node)!) {
      if (edge === via) continue;
      if (!order.has(to)) {
        visit(to, edge);
        low.set(node, Math.min(low.get(node)!, low.get(to)!));
        if (low.get(to)! > order.get(node)!) bridges.add(edge);
      } else {
        low.set(node, Math.min(low.get(node)!, order.get(to)!));
      }
    }
  };
  for (const node of adjacent.keys()) if (!order.has(node)) visit(node, -1);
  return bridges;
}

// --- reading answers -----------------------------------------------------

const QUESTION_SENSITIVE: ReadonlyMap<string, boolean> = new Map(
  F01_INSIGHT_QUESTIONS.map((q) => [q.key, q.sensitive]),
);

// The question as asked today, or as v12 last asked it when v13 no longer
// does (TES-1658), so its earlier answers stay readable.
function fieldOf(key: string): BmiQuizField | null {
  return (
    bmiQuizV2.fields.find((f) => f.key === key) ??
    bmiQuizV12.fields.find((f) => f.key === key) ??
    null
  );
}

/**
 * Her answer to one question as today's option codes. Returns null when her
 * instrument did not ask it ("not asked" is never "no answer") and [] when it
 * was asked and left empty. Unknown values are dropped. Free text never
 * passes, because only option codes of the current field are kept.
 */
export function readInsightAnswer(
  respondent: Pick<F01InsightRespondent, "answers" | "templateVersion">,
  key: string,
): string[] | null {
  const instrument = f01InsightInstrument(respondent.templateVersion);
  if (!instrument) return null;
  const answers = respondent.answers as Record<string, BmiQuizAnswerValue>;
  if (!visibleBmiQuizFields(instrument, answers).some((f) => f.key === key)) {
    return null;
  }
  const field = fieldOf(key);
  if (!field) return null;
  const raw = answers[key];
  const values =
    key === "desiredOutcome"
      ? readDesiredOutcome(raw)
      : typeof raw === "string"
        ? [raw]
        : Array.isArray(raw)
          ? raw
          : [];
  const offered = new Set((field.options ?? []).map((o) => o.value));
  return [...new Set(values.filter((v) => offered.has(v)))];
}

// --- the report ----------------------------------------------------------

export function f01InsightPeriodOf(day: string, period: string): boolean {
  if (period === "all") return true;
  const month = day.slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(period)) return month === period;
  const quarter = `${day.slice(0, 4)}-Q${Math.ceil(Number(day.slice(5, 7)) / 3)}`;
  return quarter === period;
}

/**
 * Every month, then every quarter, from her first answer to `today`, each list
 * newest first, then "all".
 */
export function f01InsightPeriods(
  respondents: readonly Pick<F01InsightRespondent, "answeredOn">[],
  today: string,
): string[] {
  const first = respondents.reduce(
    (min, r) => (r.answeredOn < min ? r.answeredOn : min),
    today,
  );
  const months: string[] = [];
  const quarters: string[] = [];
  let y = Number(first.slice(0, 4));
  let m = Number(first.slice(5, 7));
  const end = today.slice(0, 7);
  for (;;) {
    const month = `${y}-${String(m).padStart(2, "0")}`;
    if (month > end) break;
    months.unshift(month);
    const quarter = `${y}-Q${Math.ceil(m / 3)}`;
    if (quarters[0] !== quarter) quarters.unshift(quarter);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return [...months, ...quarters, "all"];
}

const COHORT_LABELS: Record<F01InsightCohort, string> = {
  booked_meeting: "Bokat vårdmöte",
  completed_meeting: "Genomfört vårdmöte",
  started_subscription: "Startat abonnemang",
};

const CHANNEL_LABELS: Record<F01InsightChannel, string> = {
  web_mobile: "Webb, mobil",
  web_desktop: "Webb, dator",
  web_unknown: "Webb, okänd enhet",
  app: "App",
};

interface Group {
  key: string;
  label: string;
  members: F01InsightRespondent[];
}

function groupsFor(
  population: F01InsightPopulation,
  breakdown: F01InsightBreakdown,
  all: F01InsightRespondent[],
): Group[] {
  const web = population === "web_unidentified";
  const total: Group = {
    key: "all",
    label: web ? "Alla webbsvar utan identifiering" : "Alla som genomfört F-01",
    members: all,
  };
  if (breakdown === "cohort") {
    return [
      total,
      ...F01_INSIGHT_COHORTS.map((c) => ({
        key: c,
        label: COHORT_LABELS[c],
        members: all.filter((r) => r.cohorts[c]),
      })),
    ];
  }
  if (breakdown === "channel") {
    return [
      total,
      // A web answer has no app channel, so that column is not drawn at all.
      ...(web ? F01_INSIGHT_WEB_CHANNELS : F01_INSIGHT_CHANNELS).map((c) => ({
        key: c,
        label: CHANNEL_LABELS[c],
        members: all.filter((r) => r.channel === c),
      })),
    ];
  }
  return [
    total,
    ...F01_INSIGHT_VERSIONS.map((v) => ({
      key: `v${v}`,
      label: `Version ${v}`,
      members: all.filter((r) => r.templateVersion === v),
    })),
  ];
}

/**
 * Every [parent, child] pair of groups whose child lies wholly inside the
 * parent in this data: "everyone" contains each group, and "completed a
 * meeting" usually lies inside "booked a meeting". Read from the members, so
 * an overlap that is not a containment is never treated as one.
 */
function nestedPairs(groups: readonly Group[]): Array<[number, number]> {
  const sets = groups.map((g) => new Set(g.members));
  const pairs: Array<[number, number]> = [];
  groups.forEach((child, c) => {
    if (child.members.length === 0) return;
    sets.forEach((parent, p) => {
      if (p !== c && child.members.every((m) => parent.has(m))) {
        pairs.push([p, c]);
      }
    });
  });
  return pairs;
}

const PAIR_LIMIT = 15;

/**
 * The whole view, already suppressed. Pure, with no clock and no I/O. The
 * server hands it every respondent and the chosen period and breakdown, and
 * returns what it gets back.
 */
export function buildF01InsightReport(input: {
  mode: F01InsightMode;
  period: string;
  breakdown: F01InsightBreakdown;
  today: string;
  respondents: readonly F01InsightRespondent[];
  /**
   * TES-1371 — read the gated weightMedHistory question. The API passes its
   * own switch here. Off (the default) leaves the report exactly as before.
   */
  weightMedHistory?: boolean;
  /** TES-1403 — the population to count. Default `patients`. */
  population?: F01InsightPopulation;
  /** The populations the view may switch between. Default: just this one. */
  populations?: readonly F01InsightPopulation[];
}): F01InsightReport {
  const population = input.population ?? "patients";
  // Technical separation (Ö-542): one report never mixes the populations.
  // A caller that hands over the wrong rows gets an error, not a blend.
  for (const r of input.respondents) {
    if ((r.population ?? "patients") !== population) {
      throw new Error(
        `f01 insights: a ${r.population ?? "patients"} respondent in a ${population} report`,
      );
    }
  }
  if (
    !F01_INSIGHT_POPULATION_BREAKDOWNS[population].includes(input.breakdown)
  ) {
    throw new Error(
      `f01 insights: breakdown ${input.breakdown} is not allowed for ${population}`,
    );
  }
  // TES-1403 — the unidentified web answers are counted under the stricter
  // rules of Ö-543 everywhere: every number needs
  // F01_INSIGHTS_MIN_SENSITIVE_GROUP people, every question gets two-way
  // complementary suppression, shares come from rounded counts, and the
  // group sizes are suppressed as a partition.
  const strict = population === "web_unidentified";
  const base = strict
    ? F01_INSIGHTS_MIN_SENSITIVE_GROUP
    : F01_INSIGHTS_MIN_GROUP;
  const inPeriod = input.respondents.filter((r) =>
    f01InsightPeriodOf(r.answeredOn, input.period),
  );
  const readable = inPeriod.filter(
    (r) => f01InsightInstrument(r.templateVersion) !== null,
  );
  const groups = groupsFor(population, input.breakdown, readable);

  // The gated question sits right after methodsTried, the question it
  // follows up in the form.
  const asked: Array<{ key: string; sensitive: boolean; gated: boolean }> =
    F01_INSIGHT_QUESTIONS.map((q) => ({ ...q, gated: false }));
  if (input.weightMedHistory === true) {
    const at = asked.findIndex((q) => q.key === "methodsTried") + 1;
    asked.splice(
      at,
      0,
      ...F01_INSIGHT_GATED_QUESTIONS.map((q) => ({ ...q, gated: true })),
    );
  }

  const questions = asked.flatMap((q) => {
    const field = fieldOf(q.key);
    if (!field || !field.options) return [];
    const options = field.options;
    const min = q.sensitive ? F01_INSIGHTS_MIN_SENSITIVE_GROUP : base;
    const single = field.kind !== "multi_select";
    // Strict means two-way suppression. For a multi-select question a
    // group's counts do not add up to its total, so treating that total as
    // a known sum (as suppressTwoWay does) can only find more cells
    // derivable, never fewer: it hides more, which is the safe direction.
    const twoWayRules = (q.gated && single) || strict;
    const raw = groups.map((g) => {
      const read = g.members
        .map((r) => readInsightAnswer(r, q.key))
        .filter((v): v is string[] => v !== null);
      const answeredSets = read.filter((v) => v.length > 0);
      return {
        key: g.key,
        asked: read.length,
        answered: answeredSets.length,
        nonResponse: read.length - answeredSets.length,
        // Nobody in the group had a version that asked it: zeros, not a
        // hidden group. An empty group is simply hidden.
        notAsked: g.members.length > 0 && read.length === 0,
        counts: options.map(
          (o) => answeredSets.filter((v) => v.includes(o.value)).length,
        ),
      };
    });

    // Which groups and counts may be shown.
    let hidden: boolean[];
    let show: boolean[][];
    if (twoWayRules) {
      const twoWay = suppressTwoWay({
        counts: raw.map((g) => g.counts),
        totals: raw.map((g) => g.answered),
        min,
        partitioned: input.breakdown !== "cohort",
        nested: nestedPairs(groups),
      });
      hidden = raw.map((g, i) => !g.notAsked && !twoWay.groups[i]);
      show = twoWay.cells;
    } else {
      hidden = raw.map((g) => !g.notAsked && g.answered < min);
      show = raw.map((g, i) =>
        hidden[i]
          ? g.counts.map(() => false)
          : single
            ? suppressPartition(g.counts, min)
            : g.counts.map((n) => n === 0 || n >= min),
      );
    }

    // A gated question's shares come from the rounded counts, so that an
    // exact percentage of a rounded total cannot give the exact count back.
    const shareOf = (n: number, of: number, shown: boolean) =>
      q.gated || strict
        ? share(roundCount(n), roundCount(of), shown)
        : share(n, of, shown);

    return [
      {
        key: q.key,
        label: field.label.sv,
        kind: single ? ("single" as const) : ("multi" as const),
        sensitive: q.sensitive,
        groups: raw.map((g, i) => {
          // Too few answered: nothing is shown, not even "asked" and
          // "non-response", whose difference is the hidden number. A hidden
          // non-response hides "asked" for the same reason.
          const nonResponseShown =
            !hidden[i] && (g.nonResponse === 0 || g.nonResponse >= min);
          const none = { n: null, min };
          return {
            group: g.key,
            asked: nonResponseShown ? cell(g.asked, min) : none,
            answered: hidden[i] ? none : cell(g.answered, min),
            nonResponse: nonResponseShown ? cell(g.nonResponse, min) : none,
            nonResponseShare: shareOf(g.nonResponse, g.asked, nonResponseShown),
            hidden: hidden[i],
            notAsked: g.notAsked,
            options: options.map((o, j) => ({
              value: o.value,
              label: o.label.sv,
              count: show[i][j] ? cell(g.counts[j], min) : none,
              share: shareOf(g.counts[j], g.answered, show[i][j]),
            })),
          };
        }),
      },
    ];
  });

  // Group sizes. Channel and version split everyone without overlap, so
  // under the strict rules a hidden group could otherwise be read as
  // everyone minus the shown groups.
  const sizes = groups.map((g) => g.members.length);
  let sizeShown = sizes.map(() => true);
  if (strict) {
    const totalShown = sizes[0] === 0 || sizes[0] >= base;
    const parts = totalShown
      ? suppressPartition(sizes.slice(1), base)
      : sizes.slice(1).map((n) => n === 0);
    sizeShown = [totalShown, ...parts];
  }

  return {
    mode: input.mode,
    population,
    populations: [...(input.populations ?? [population])],
    period: input.period,
    breakdown: input.breakdown,
    periods: f01InsightPeriods(input.respondents, input.today),
    rules: {
      minGroup: base,
      minSensitiveGroup: F01_INSIGHTS_MIN_SENSITIVE_GROUP,
      roundTo: F01_INSIGHTS_ROUND_TO,
    },
    respondents: sizeShown[0]
      ? cell(readable.length, base)
      : { n: null, min: base },
    olderVersions: cell(inPeriod.length - readable.length, base),
    groups: groups.map((g, i) => ({
      key: g.key,
      label: g.label,
      respondents: sizeShown[i]
        ? cell(g.members.length, base)
        : { n: null, min: base },
    })),
    questions,
    pairs: answerPairs(readable, base, strict),
  };
}

function answerPairs(
  respondents: readonly F01InsightRespondent[],
  base: number,
  strict: boolean,
): F01InsightReport["pairs"] {
  type Side = { question: string; value: string };
  const sides = new Map<string, Side>();
  const counts = new Map<string, number>();
  for (const r of respondents) {
    const mine: Side[] = [];
    for (const q of F01_INSIGHT_QUESTIONS) {
      for (const value of readInsightAnswer(r, q.key) ?? []) {
        mine.push({ question: q.key, value });
      }
    }
    for (let i = 0; i < mine.length; i++) {
      for (let j = i + 1; j < mine.length; j++) {
        const ka = `${mine[i].question}:${mine[i].value}`;
        const kb = `${mine[j].question}:${mine[j].value}`;
        sides.set(ka, mine[i]);
        sides.set(kb, mine[j]);
        const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
  }
  const side = (k: string) => {
    const s = sides.get(k)!;
    const field = fieldOf(s.question)!;
    return {
      question: s.question,
      questionLabel: field.label.sv,
      value: s.value,
      label:
        field.options?.find((o) => o.value === s.value)?.label.sv ?? s.value,
    };
  };
  return (
    [...counts.entries()]
      .map(([key, n]) => {
        const [ka, kb] = key.split("|");
        const sensitive =
          QUESTION_SENSITIVE.get(sides.get(ka)!.question) === true ||
          QUESTION_SENSITIVE.get(sides.get(kb)!.question) === true;
        const min = sensitive ? F01_INSIGHTS_MIN_SENSITIVE_GROUP : base;
        return { ka, kb, n, min };
      })
      // Only pairs that are shown are listed, so the list never reveals that
      // a small combination exists.
      .filter((p) => p.n >= p.min)
      .sort((x, y) => y.n - x.n || (x.ka + x.kb < y.ka + y.kb ? -1 : 1))
      .slice(0, PAIR_LIMIT)
      .map((p) => ({
        a: side(p.ka),
        b: side(p.kb),
        count: cell(p.n, p.min),
        share: strict
          ? share(roundCount(p.n), roundCount(respondents.length), true)
          : share(p.n, respondents.length, true),
      }))
  );
}
